<template>
  <div class="incoming-calls-toast-container">
    <div
      v-for="alert in activeAlerts"
      :key="alert.id"
      class="call-toast"
      :class="alert.status"
    >
      <div class="toast-content" @click="goToCallerDetail(primaryCaller(alert.call))">
        <div class="caller-info">
          <div class="caller-avatar">
            <img
              v-if="primaryCaller(alert.call)?.image"
              :src="primaryCaller(alert.call).image"
              class="avatar-img"
            />
            <div v-else class="avatar-placeholder">
              <CIcon name="cil-user" class="avatar-icon" />
            </div>
          </div>

          <div class="caller-details">
            <div class="caller-name">{{ formatCallerName(alert.call) }}</div>
            <div class="caller-company small" v-if="primaryCaller(alert.call)?.company">{{ primaryCaller(alert.call)?.company?.name }}</div>
            <div class="caller-number">
              <CIcon name="cil-phone" class="mr-1" />
              {{ primaryCaller(alert.call)?.phone || alert.call.callernum }}
            </div>
          </div>
        </div>

        <div class="call-details">
          <div class="call-flow">
            <span class="from">{{ formatCallerType(alert.call) | uppercase }}</span>
          </div>

          <div class="call-meta">
            <span class="time">{{ $globalFunc.formatTime(alert.call.alloc_time) }}</span>
          </div>

          <div v-if="hasMultipleCallers(alert.call)" class="caller-match-list" @click.stop>
            <button
              v-for="caller in callerMatches(alert.call)"
              :key="callerKey(caller)"
              type="button"
              class="caller-match"
              @click.stop="goToCallerDetail(caller)"
            >
              <span class="caller-match-type">{{ caller.type | uppercase }}</span>
              <span class="caller-match-name">{{ formatCallerLabel(caller) }}</span>
              <span class="caller-match-phone" v-if="caller.phone">{{ caller.phone }}</span>
            </button>
          </div>
        </div>
      </div>
      <button class="toast-close" @click="closeAlert(alert.id)">
        <CIcon name="cil-x" />
      </button>
      
      <div class="toast-progress">
        <div class="progress-bar" :style="{ width: alert.progress + '%' }"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'IncomingCallsAlert',
  data() {
    return {
      lastShownCalls: new Set(), // Track which calls we've already shown
      activeAlerts: [], // Array of active toast alerts
      alertCounter: 0, // Unique ID counter for alerts
    };
  },
  computed: {
    ...mapGetters(['voipRingingCalls']),
  },
  watch: {
    voipRingingCalls: { handler: 'handleIncomingCallsChange', deep: true },
  },
  methods: {
    handleIncomingCallsChange() {
      const activeIds = new Set(this.activeAlerts.map(a => a.call?.uniqueid));

      this.voipRingingCalls.forEach(call => {
        const id = call.uniqueid;
        if (!id) return;

        // Skip if already shown or currently active
        if (this.lastShownCalls.has(id) || activeIds.has(id)) return;

        this.lastShownCalls.add(id);
        this.showCallToast(call);
        this.playToastSound();
      });
    },

    showCallToast(call) {
      const alertId = ++this.alertCounter;
      const alert = {
        id: alertId,
        call: call,
        status: 'ringing',
        progress: 100,
        startTime: Date.now(),
      };

      this.activeAlerts.push(alert);

      // Start progress bar countdown
      const duration = 30000; // 30 seconds
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - alert.startTime;
        const remaining = Math.max(0, duration - elapsed);
        alert.progress = (remaining / duration) * 100;

        if (remaining <= 0) {
          clearInterval(progressInterval);
          this.closeAlert(alertId);
        }
      }, 100);

      // Auto close after "duration" seconds
      setTimeout(() => {
        this.closeAlert(alertId);
      }, duration);
    },

    closeAlert(alertId) {
      // Find and remove from active alerts
      const alert = this.activeAlerts.find(a => a.id === alertId);
      if (alert?.call?.uniqueid) {
        // Allow re-show after manual close
        this.lastShownCalls.delete(alert.call.uniqueid);
      }
      this.activeAlerts = this.activeAlerts.filter(a => a.id !== alertId);
    },

    formatCallerName(call) {
      const caller = this.primaryCaller(call);
      if (caller?.name) return caller.name;
      if (call.callername) return call.callername;
      if (call.callernum) return `Caller ${call.callernum}`;
      return 'Unknown Caller';
    },

    formatCallerType(call) {
      const matches = this.callerMatches(call);
      if (matches.length > 1) return `${matches.length} matches`;
      return this.primaryCaller(call)?.type || 'Unknown';
    },

    formatCallerLabel(caller) {
      return caller?.name || caller?.email || caller?.phone || 'Unknown';
    },

    callerMatches(call) {
      if (Array.isArray(call?.callers) && call.callers.length) {
        return call.callers;
      }

      return call?.caller ? [call.caller] : [];
    },

    primaryCaller(call) {
      return this.callerMatches(call)[0] || call?.caller || null;
    },

    hasMultipleCallers(call) {
      return this.callerMatches(call).length > 1;
    },

    callerKey(caller) {
      return `${caller?.type || 'unknown'}-${caller?.id || caller?.phone || caller?.email || 'missing'}`;
    },

    getCallerDetailRoute(caller) {
      switch (caller?.type) {
        case 'user':
          return { name: 'User', params: { id: caller.id } };
        case 'company':
          return caller.company?.id ? { name: 'Edit Company', params: { id: caller.company.id } } : null;
        case 'courier':
          return { name: 'Edit Courier', params: { id: caller.id } };
        case 'restaurant':
          return { name: 'View Restaurant', params: { id: caller.id } };
        default:
          return null;
      }
    },

    goToCallerDetail(caller) {
      if (this.isOrderAdmin && !this.isSuperAdmin) return;

      const route = this.getCallerDetailRoute(caller);

      if (route) {
        // Avoided redundant navigation to current location
        if (
          this.$route.name === route.name &&
          Object.keys(route.params || {}).every(
            key => this.$route.params[key] == route.params[key]
          ) &&
          Object.keys(this.$route.params || {}).every(
            key => route.params[key] == this.$route.params[key]
          )
        ) {
          return;
        }

        const routeData = this.$router.resolve(route);
        window.open(routeData.href, "_blank");
      }
    },

    playToastSound() {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const ctx = new AudioContext();
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(880, ctx.currentTime); // A5 tone
        gainNode.gain.setValueAtTime(0.001, ctx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.06, ctx.currentTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.start();
        oscillator.stop(ctx.currentTime + 0.28);

        // Close the context shortly after to release resources
        oscillator.onended = () => {
          try { ctx.close(); } catch (e) { /* no-op */ }
        };
      } catch (e) {
        // no-op if audio context not available or blocked
      }
    },
  },
  mounted() {
    // Listen global event from FloatingPhoneButton
    window.addEventListener('voip-show-incoming-alerts', this.handleIncomingCallsChange);
  },
  beforeDestroy() {
    window.removeEventListener('voip-show-incoming-alerts', this.handleIncomingCallsChange);
  }
};
</script>

<style scoped>
.incoming-calls-toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 500px;
  pointer-events: none;
}

.call-toast {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: none;
  overflow: hidden;
  pointer-events: auto;
  animation: slideInRight 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  position: relative;
  max-width: 500px;
  display: flex;
  align-items: start;
  gap: 12px;
  padding: 16px 20px;
  min-height: 80px;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
}

.call-toast:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15),
    0 4px 12px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.call-toast.ringing {
  background: linear-gradient(135deg,
    #f6fbff 0%,
    #e6f3ff 50%,
    #d6ecff 100%);
  /* Çok daha açık mavi (info) tonlarında, yüksek okunabilirlik için */
  border-left: 4px solid #60a5fa;
  box-shadow: 0 8px 32px rgba(96, 165, 250, 0.10),
    0 2px 8px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

@keyframes slideInRight {
  0% {
    transform: translateX(100%) scale(0.8);
    opacity: 0;
  }

  60% {
    transform: translateX(-8px) scale(1.02);
    opacity: 0.8;
  }

  100% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
}

.toast-content {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
}

.caller-info {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 160px;
}

.caller-avatar {
  position: relative;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}

.avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
}

.avatar-img:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15),
    0 3px 10px rgba(0, 0, 0, 0.08);
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(135deg,
      rgba(139, 92, 246, 0.1) 0%,
      rgba(59, 130, 246, 0.05) 50%,
      rgba(16, 185, 129, 0.1) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
}

.avatar-placeholder:hover {
  transform: scale(1.05);
  background: linear-gradient(135deg,
      rgba(139, 92, 246, 0.15) 0%,
      rgba(59, 130, 246, 0.1) 50%,
      rgba(16, 185, 129, 0.15) 100%);
}

.avatar-icon {
  font-size: 1.4rem;
  color: #3b82f6;
}

.caller-details {
  flex: 1;
  min-width: 0;
}

.caller-name {
  font-size: 1rem;
  font-weight: 700;
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.025em;
}

.caller-company {
  font-size: 70%;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.025em;
}

.caller-number {
  font-size: 0.85rem;
  color: #6b7280;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.caller-match-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 4px;
}

.caller-match {
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  align-items: center;
  gap: 4px 8px;
  width: 100%;
  padding: 5px 7px;
  border: 1px solid rgba(203, 213, 225, 0.8);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.72);
  color: #334155;
  text-align: left;
  cursor: pointer;
}

.caller-match:hover {
  background: rgba(239, 246, 255, 0.95);
  border-color: rgba(96, 165, 250, 0.65);
}

.caller-match-type {
  min-width: 0;
  padding: 2px 6px;
  border-radius: 4px;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 0.68rem;
  font-weight: 700;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
}

.caller-match-name,
.caller-match-phone {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.caller-match-name {
  font-weight: 600;
}

.caller-match-phone {
  grid-column: 2;
  color: #64748b;
  font-size: 0.72rem;
}

.call-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.call-flow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #374151;
  padding: 4px 8px;
  /* background: rgba(249, 250, 251, 0.8); */
  border-radius: 6px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(229, 231, 235, 0.3);

  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  letter-spacing: 0.025em;
}

.call-meta {
  display: flex;
  gap: 8px;
  font-size: 0.75rem;
}

.time {
  background: rgba(243, 244, 246, 0.8);
  color: #6b7280;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(229, 231, 235, 0.3);
}

.toast-close {
  background: rgba(255, 255, 255, 0.8);
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(229, 231, 235, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.toast-close:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
}

.toast-close:active {
  transform: scale(0.95);
}

.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(229, 231, 235, 0.3);
  backdrop-filter: blur(10px);
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg,
      #e5e7eb 0%,
      #d1d5db 50%,
      #9ca3af 100%);
  transition: width 0.1s linear;
  box-shadow: 0 0 8px rgba(209, 213, 219, 0.5);
}

/* Responsive Design */
@media (max-width: 768px) {
  .incoming-calls-toast-container {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
  }

  .call-toast {
    max-width: none;
    padding: 10px 12px;
    min-height: 50px;
  }

  .toast-content {
    gap: 12px;
  }

  .caller-info {
    min-width: 120px;
    gap: 10px;
  }

  .caller-avatar {
    width: 42px;
    height: 42px;
  }

  .avatar-icon {
    font-size: 1.1rem;
  }

  .caller-name {
    font-size: 0.85rem;
  }

  .caller-number {
    font-size: 0.75rem;
  }

  .call-flow {
    font-size: 0.75rem;
  }

  .call-meta {
    font-size: 0.65rem;
  }
}

@media (max-width: 480px) {
  .incoming-calls-toast-container {
    top: 5px;
    right: 5px;
    left: 5px;
  }

  .call-toast {
    padding: 8px 10px;
    min-height: 45px;
  }

  .toast-content {
    gap: 8px;
  }

  .caller-info {
    min-width: 100px;
    gap: 8px;
  }

  .caller-avatar {
    width: 36px;
    height: 36px;
  }

  .avatar-icon {
    font-size: 1rem;
  }

  .caller-name {
    font-size: 0.8rem;
  }

  .caller-number {
    font-size: 0.7rem;
  }

  .call-flow {
    font-size: 0.7rem;
  }

  .call-meta {
    font-size: 0.6rem;
  }

  .toast-close {
    padding: 3px;
  }
}
</style>
